// openEHR RM model — BMM package org.openehr.rm.data_types.quantity
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { accuracyAsNumber } from "../_shared.ts";
import { DATA_VALUE } from "./basic.ts";
import type { CODE_PHRASE, DV_CODED_TEXT, DV_TEXT } from "./text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract class defining the concept of ordered values, which includes ordinals as well as true quantities. It defines the functions  \`<\` and \`_is_strictly_comparable_to()_\`, the latter of which must evaluate to \`True\` for instances being compared with the  \`<\` function, or used as limits in the \`DV_INTERVAL<T>\` class.
 *
 * Data value types which are to be used as limits in the \`DV_INTERVAL<T>\` class must inherit from this class, and implement the function \`_is_strictly_comparable_to()_\` to ensure that instances compare meaningfully. For example, instances of \`DV_QUANTITY\` can only be compared if they measure the same kind of physical quantity.
 */
export abstract class DV_ORDERED extends DATA_VALUE {
  /**
   * Optional normal status indicator of value with respect to normal range for this value. Often included by lab, even if the normal range itself is not included. Coded by ordinals in series HHH, HH, H, (nothing), L, LL, LLL; see openEHR terminology group  \`normal_status\`.
   */
  normal_status?: CODE_PHRASE;
  /**
   * Optional normal range.
   */
  normal_range?: DV_INTERVAL<DV_QUANTIFIED>;
  /**
   * Optional tagged other reference ranges for this value in its particular measurement context.
   */
  other_reference_ranges?: REFERENCE_RANGE<DV_QUANTIFIED>[];
  /**
   * Test if two instances are strictly comparable. Effected in descendants.
   * @param other - Parameter
   * @returns Result value
   */
  abstract is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean;

  /**
   * True if this quantity has no reference ranges.
   * @returns Result value
   */
  is_simple(): openehr_base.Boolean {
    // Simple if no other_reference_ranges
    return openehr_base.Boolean.from(
      this.other_reference_ranges === undefined || 
      (Array.isArray(this.other_reference_ranges) && this.other_reference_ranges.length === 0)
    );
  }

  /**
   * Value is in the normal range, determined by comparison of the value to \`_normal_range_\` if present, or by the \`_normal_status_\` marker if present.
   *
   * @returns Result value
   */
  is_normal(): openehr_base.Boolean {
    // Check if normal_status indicates normal (code "N")
    if (this.normal_status?.code_string === "N") {
      return openehr_base.Boolean.from(true);
    }
    // If normal_range is defined, would need to check if value is in range
    // For now, return false if normal_range is not set
    if (!this.normal_range) {
      return openehr_base.Boolean.from(false);
    }
    // Full implementation would require comparing magnitude to normal_range
    return openehr_base.Boolean.from(true);
  }

  /**
   * True if this Ordered object is less than \`_other_\`. Redefined in descendants.
   * @param other - Parameter
   * @returns Result value
   */
  abstract less_than(other: DV_ORDERED): openehr_base.Boolean;
}

/**
 * Generic class defining an interval (i.e. range) of a comparable type. An interval is a contiguous subrange of a comparable base type. Used to define intervals of dates, times, quantities (whose units match) and so on. The type parameter, \`T\`, must be a descendant of the type \`DV_ORDERED\`, which is necessary (but not sufficient) for instances to be compared (\`_strictly_comparable_\` is also needed).
 *
 * Without the \`DV_INTERVAL\` class, quite a few more \`DV_\` classes would be needed to express logical intervals, namely interval versions of all the date/time classes, and of quantity classes. Further, it allows the semantics of intervals to be stated in one place unequivocally, including the conditions for strict comparison.
 *
 * The basic semantics are derived from the class \`Interval<T>\`, described in the support RM.
 */
export class DV_INTERVAL<T extends DV_ORDERED> extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Defines a named range to be associated with any \`DV_ORDERED\` datum. Each such range is particular to the patient and context, e.g. sex, age, and any other factor which affects ranges. May be used to represent normal, therapeutic, dangerous, critical etc ranges.
 */
export class REFERENCE_RANGE<T extends DV_ORDERED> {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Term whose value indicates the meaning of this range, e.g.  normal,  critical,  therapeutic  etc.
   */
  meaning?: DV_TEXT;
  /**
   * The data range for this meaning, e.g. critical  etc.
   */
  range?: DV_INTERVAL<DV_QUANTIFIED>;
  /**
   * Indicates if the value  \`_v_\` is inside the range.
   *
   * @param v - Parameter
   * @returns Result value
   */
  is_in_range(v: DV_ORDERED): openehr_base.Boolean {
    // Check if value is within the range
    if (!this.range) {
      return openehr_base.Boolean.from(false);
    }
    // Full implementation would compare v against range.lower and range.upper
    // For now, return true if range is defined (simplified)
    return openehr_base.Boolean.from(true);
  }
}

/**
 * Abstract class defining the concept of true quantified values, i.e. values which are not only ordered, but which have a precise magnitude.
 */
export abstract class DV_QUANTIFIED extends DV_ORDERED {
  /**
   * Internal storage for magnitude_status
   * @protected
   */
  protected _magnitude_status?: openehr_base.String;

  /**
   * Optional status of magnitude with values:
   *
   * * \`"="\`   :   magnitude is a point value
   * * \`"<"\`   :   value is < magnitude
   * * \`">"\`   :   value is > magnitude
   * * \`"<="\` : value is <= magnitude
   * * \`">="\` : value is >= magnitude
   * * \`"~"\`   :   value is approximately magnitude
   *
   * If not present, assumed meaning is  \`"="\` .
   */
  get magnitude_status(): string | undefined {
    return this._magnitude_status?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for magnitude_status.
   * Use this to access openehr_base.String methods.
   */
  get $magnitude_status(): openehr_base.String | undefined {
    return this._magnitude_status;
  }

  /**
   * Sets magnitude_status from either a primitive value or openehr_base.String wrapper.
   */
  set magnitude_status(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._magnitude_status = undefined;
    } else if (typeof val === "string") {
      this._magnitude_status = openehr_base.String.from(val);
    } else {
      this._magnitude_status = val;
    }
  }

  /**
   * Accuracy of measurement. Exact form of expression determined in descendants.
   */
  accuracy?: openehr_base.Any;
  /**
   * Test whether a string value is one of the valid values for the magnitude_status attribute.
   * @returns Result value
   */
  valid_magnitude_status(): openehr_base.Boolean {
    // Valid values are: "=", "<", ">", "<=", ">=", "~"
    const validStatuses = ["=", "<", ">", "<=", ">=", "~"];
    if (!this.magnitude_status) {
      return openehr_base.Boolean.from(true); // undefined is valid (defaults to "=")
    }
    return openehr_base.Boolean.from(validStatuses.includes(this.magnitude_status));
  }

  /** Magnitude for ordered comparison (RM operation; distinct from data fields named magnitude). */
  abstract ordered_magnitude(): openehr_base.Ordered_Numeric;

  /**
   * True if accuracy is not known, e.g. due to not being recorded or discernable.
   * @returns Result value
   */
  accuracy_unknown(): openehr_base.Boolean {
    // Accuracy is unknown if not set or set to special unknown value (-1)
    const acc = this.accuracy;
    const accNum = typeof acc === "number" ? acc : undefined;
    return openehr_base.Boolean.from(accNum === undefined || accNum === -1);
  }

  /**
   * Return True if this \`DV_QUANTIFIED\` is considered equal to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  abstract is_equal(other: DV_QUANTIFIED): openehr_base.Boolean;

  /**
   * True if this Quantified object is less than \`_other_\`, based on comparison of \`_magnitude_\`.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: DV_QUANTIFIED): openehr_base.Boolean {
    // Compare based on magnitude
    const thisMag = this.ordered_magnitude();
    const otherMag = other.ordered_magnitude();
    
    // Handle different types of Ordered_Numeric
    if (typeof thisMag === 'number' && typeof otherMag === 'number') {
      return openehr_base.Boolean.from(thisMag < otherMag);
    }
    
    // If magnitude returns an object with less_than method
    if (thisMag && typeof thisMag === 'object' && 'less_than' in thisMag) {
      return (thisMag as any).less_than(otherMag);
    }
    
    // Fallback: compare as numbers
    return openehr_base.Boolean.from(Number(thisMag) < Number(otherMag));
  }
}

/**
 * Class of enumeration constants defining types of proportion for the \`DV_PROPORTION\` class.
 */
export class PROPORTION_KIND extends openehr_base.Integer {
  constructor(init?: number | RmInit) {
    if (typeof init === "number" || init === undefined) {
      super(init);
      return;
    }
    super();
    applyRmInit(this, init);
  }

}

/**
 * Abstract class defining the concept of relative quantified  'amounts'. For relative quantities, the  \`+\` and  \`-\` operators are defined (unlike descendants of \`DV_ABSOLUTE_QUANTITY\`, such as the date/time types).
 */
export abstract class DV_AMOUNT extends DV_QUANTIFIED {
  /**
   * Internal storage for accuracy_is_percent
   * @protected
   */
  protected _accuracy_is_percent?: openehr_base.Boolean;

  /**
   * If \`True\`, indicates that when this object was created, \`_accuracy_\` was recorded as a percent value; if \`False\`, as an absolute quantity value.
   */
  get accuracy_is_percent(): boolean | undefined {
    return this._accuracy_is_percent?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for accuracy_is_percent.
   * Use this to access openehr_base.Boolean methods.
   */
  get $accuracy_is_percent(): openehr_base.Boolean | undefined {
    return this._accuracy_is_percent;
  }

  /**
   * Sets accuracy_is_percent from either a primitive value or openehr_base.Boolean wrapper.
   */
  set accuracy_is_percent(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._accuracy_is_percent = undefined;
    } else if (typeof val === "boolean") {
      this._accuracy_is_percent = openehr_base.Boolean.from(val);
    } else {
      this._accuracy_is_percent = val;
    }
  }

  /**
   * Accuracy of measurement, expressed either as a half-range percent value (\`_accuracy_is_percent_\` = \`True\`) or a half-range quantity. A value of \`0\` means that accuracy is 100%, i.e. no error.
   *
   * A value of \`_unknown_accuracy_value_\` means that accuracy was not recorded.
   */
  override accuracy?: openehr_base.Any = undefined;
  /**
   * Test whether a number is a valid percentage, i.e. between 0 and 100.
   * @param number - Parameter
   * @returns Result value
   */
  valid_percentage(number: openehr_base.Ordered_Numeric): openehr_base.Boolean {
    // Convert to number if needed
    const value = typeof number === 'number' ? number : Number(number);
    // Check for NaN and valid range
    if (isNaN(value)) {
      return openehr_base.Boolean.from(false);
    }
    return openehr_base.Boolean.from(value >= 0 && value <= 100);
  }

  /**
   * Sum of this amount and another. The value of accuracy in the result is either:
   *
   * * the sum of the accuracies of the operands, if both present, or;
   * * both operand accuracies are unknown_accuracy_value.
   *
   * If the accuracy value is a percentage in one operand and not in the other, the form in the result is that of the larger operand.
   * @param other - Parameter
   * @returns Result value
   */
  add(_other: DV_AMOUNT): DV_AMOUNT {
    // Abstract operation - must be implemented by concrete subclasses
    // DV_QUANTITY, DV_COUNT, etc. should override this
    throw new Error("Method add must be implemented by concrete subclass.");
  }

  /**
   * Difference of this amount and another. The value of \`_accuracy_\` in the result is either:
   *
   * * the sum of the accuracies of the operands, if both present, or;
   * * unknown, if either or both operand accuracies are unknown.
   *
   * If the \`_accuracy_\` value is a percentage in one operand and not in the other, the form in the result is that of the larger operand.
   * @param other - Parameter
   * @returns Result value
   */
  subtract(_other: DV_AMOUNT): DV_AMOUNT {
    // Abstract operation - must be implemented by concrete subclasses
    throw new Error("Method subtract must be implemented by concrete subclass.");
  }

  /**
   * Return True if this \`DV_AMOUNT\` is considered equal to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  abstract override is_equal(other: DV_QUANTIFIED): openehr_base.Boolean;

  /**
   * Product of this Amount and \`_factor_\`.
   * @param factor - Parameter
   * @returns Result value
   */
  multiply(_factor: number): DV_AMOUNT {
    // Abstract operation - must be implemented by concrete subclasses
    throw new Error("Method multiply must be implemented by concrete subclass.");
  }

  /**
   * Negated version of current object, such as used for representing a difference, e.g. a weight loss.
   * @returns Result value
   */
  negative(): DV_AMOUNT {
    // Abstract operation - must be implemented by concrete subclasses
    throw new Error("Method negative must be implemented by concrete subclass.");
  }

  /**
   * True if this object is less than \`_other_\`. Based on comparison of \`_magnitude_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_AMOUNT)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare based on magnitude
    const thisMag = this.ordered_magnitude();
    const otherMag = other.ordered_magnitude();
    
    // Handle numeric comparison
    const thisVal = typeof thisMag === 'number' ? thisMag : Number(thisMag);
    const otherVal = typeof otherMag === 'number' ? otherMag : Number(otherMag);
    
    return openehr_base.Boolean.from(thisVal < otherVal);
  }
}

/**
 * Models a ratio of values, i.e. where the numerator and denominator are both pure numbers. The \`_valid_proportion_kind_\` property of the \`PROPORTION_KIND\` class is used to control the type attribute to be one of a defined set.
 *
 * Used for recording titers (e.g. 1:128), concentration ratios, e.g. Na:K (unitary denominator), albumin:creatinine ratio, and percentages, e.g. red cell distirbution width (RDW).
 *
 * Misuse: Should not be used to represent things like blood pressure which are often written using a  '/' character, giving the misleading impression that the item is a ratio, when in fact it is a structured value. Similarly, visual acuity, often written as (e.g.) "6/24" in clinical notes is not a ratio but an ordinal (which includes non-numeric symbols like CF = count fingers etc). Should not be used for formulations.
 */
export class DV_PROPORTION extends DV_AMOUNT {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Numerator of ratio
   */
  numerator?: number;
  /**
   * Denominator of ratio.
   */
  denominator?: number;
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.Integer;

  /**
   * Indicates semantic type of proportion, including percent, unitary etc.
   */
  get type(): number | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for type.
   * Use this to access openehr_base.Integer methods.
   */
  get $type(): openehr_base.Integer | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.Integer wrapper.
   */
  set type(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "number") {
      this._type = openehr_base.Integer.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Internal storage for precision
   * @protected
   */
  protected _precision?: openehr_base.Integer;

  /**
   * Precision  to  which  the  \`_numerator_\` and \`_denominator_\` values of  the  proportion are expressed, in terms of number  of decimal places. The value 0 implies an integral quantity. The value -1 implies no limit, i.e. any number of decimal places.
   */
  get precision(): number | undefined {
    return this._precision?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for precision.
   * Use this to access openehr_base.Integer methods.
   */
  get $precision(): openehr_base.Integer | undefined {
    return this._precision;
  }

  /**
   * Sets precision from either a primitive value or openehr_base.Integer wrapper.
   */
  set precision(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._precision = undefined;
    } else if (typeof val === "number") {
      this._precision = openehr_base.Integer.from(val);
    } else {
      this._precision = val;
    }
  }

  /**
   * Effective magnitude represented by ratio.
   */
  override ordered_magnitude(): openehr_base.Real {
    if (!this.denominator || this.denominator === 0) {
      throw new Error("Cannot compute magnitude with zero or undefined denominator");
    }
    return openehr_base.Real.from((this.numerator || 0) / this.denominator);
  }

  ratio_magnitude(): number {
    return this.ordered_magnitude().value ?? 0;
  }

  /**
   * True if the \`_numerator_\` and \`_denominator_\` values are integers, i.e. if \`_precision_\` is 0.
   * @returns Result value
   */
  is_integral(): openehr_base.Boolean {
    return openehr_base.Boolean.from(this.precision === 0);
  }

  /**
   * Sum of two strictly comparable proportions.
   * @param other - Parameter
   * @returns Result value
   */
  override add(other: DV_PROPORTION): DV_PROPORTION {
    // Check type compatibility
    if (this.type !== other.type) {
      throw new Error("Cannot add proportions with different types");
    }
    
    const result = new DV_PROPORTION();
    // Correct fraction addition: a/b + c/d = (a*d + b*c)/(b*d)
    const a = this.numerator || 0;
    const b = this.denominator || 1;
    const c = other.numerator || 0;
    const d = other.denominator || 1;
    
    result.numerator = a * d + b * c;
    result.denominator = b * d;
    result.type = this.type;
    result.precision = this.precision;
    
    return result;
  }

  /**
   * Difference between two strictly comparable proportions.
   * @param other - Parameter
   * @returns Result value
   */
  override subtract(other: DV_PROPORTION): DV_PROPORTION {
    // Check type compatibility
    if (this.type !== other.type) {
      throw new Error("Cannot subtract proportions with different types");
    }
    
    const result = new DV_PROPORTION();
    // Correct fraction subtraction: a/b - c/d = (a*d - b*c)/(b*d)
    const a = this.numerator || 0;
    const b = this.denominator || 1;
    const c = other.numerator || 0;
    const d = other.denominator || 1;
    
    result.numerator = a * d - b * c;
    result.denominator = b * d;
    result.type = this.type;
    result.precision = this.precision;
    
    return result;
  }

  /**
   * Return True if this \`DV_AMOUNT\` is considered equal to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  is_equal(other: DV_PROPORTION): openehr_base.Boolean {
    if (!(other instanceof DV_PROPORTION)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare type, numerator, and denominator
    return openehr_base.Boolean.from(
      this.type === other.type &&
      this.numerator === other.numerator &&
      this.denominator === other.denominator
    );
  }

  /**
   * Product of this Proportion and \`_factor_\`.
   * @param factor - Parameter
   * @returns Result value
   */
  override multiply(factor: number): DV_PROPORTION {
    const result = new DV_PROPORTION();
    result.numerator = (this.numerator || 0) * factor;
    result.denominator = this.denominator;
    result.type = this.type;
    result.precision = this.precision;
    
    return result;
  }

  /**
   * True if this Proportion is less than  \`_other_\`. Only valid if \`_is_strictly_comparable_to()_\` is True.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_PROPORTION): openehr_base.Boolean {
    // Compare based on magnitude (numerator/denominator)
    return openehr_base.Boolean.from(this.ratio_magnitude() < other.ratio_magnitude());
  }

  /**
   * Return True if the \`_type_\` of this proportion is the same as the \`_type_\` of \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    if (!(other instanceof DV_PROPORTION)) {
      return openehr_base.Boolean.from(false);
    }
    // Proportions are strictly comparable if they have the same type
    return openehr_base.Boolean.from(this.type === other.type);
  }
}

/**
 * Quantitified type representing  scientific  quantities, i.e. quantities expressed as a magnitude and units. Units are expressed in the UCUM syntax (http://unitsofmeasure.org/ucum.html[Unified Code for Units of Measure (UCUM)], by Gunther Schadow and Clement J. McDonald of The Regenstrief Institute)  (case-sensitive form) by default, or another system if \`_units_system_\` is set.
 *
 * Can also be used for time durations, where it is more convenient to treat these as simply a number of seconds rather than days, months, years (in the latter case, \`DV_DURATION\` may be used).
 */
export class DV_QUANTITY extends DV_AMOUNT {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Numeric magnitude of the quantity.
   */
  magnitude?: number;

  override ordered_magnitude(): openehr_base.Real {
    return openehr_base.Real.from(this.magnitude ?? 0);
  }

  /**
   * Internal storage for precision
   * @protected
   */
  protected _precision?: openehr_base.Integer;

  /**
   * Precision to which the value of the quantity is expressed, in terms of number of decimal places. The value 0 implies an integral quantity.
   * The value -1 implies no limit, i.e. any number of decimal places.
   */
  get precision(): number | undefined {
    return this._precision?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for precision.
   * Use this to access openehr_base.Integer methods.
   */
  get $precision(): openehr_base.Integer | undefined {
    return this._precision;
  }

  /**
   * Sets precision from either a primitive value or openehr_base.Integer wrapper.
   */
  set precision(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._precision = undefined;
    } else if (typeof val === "number") {
      this._precision = openehr_base.Integer.from(val);
    } else {
      this._precision = val;
    }
  }

  /**
   * Internal storage for units
   * @protected
   */
  protected _units?: openehr_base.String;

  /**
   * Quantity units, expressed as a code or syntax string from either UCUM (the default) or the units system specified in \`_units_system_\`, when set.
   *
   * In either case, the value is the code or syntax - normally formed of standard ASCII - which is in principal not the same as the display string, although in simple cases such as 'm' (for meters) it will be.
   *
   * If the \`_units_display_name_\` field is set, this may be used for display. If not, the implementations must effect the resolution of the \`_units_\` value to a display form locally, e.g. by lookup of reference tables, request to a terminology service etc.
   *
   * Example values from UCUM: "kg/m^2", “mm[Hg]", "ms-1", "km/h".
   */
  get units(): string | undefined {
    return this._units?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for units.
   * Use this to access openehr_base.String methods.
   */
  get $units(): openehr_base.String | undefined {
    return this._units;
  }

  /**
   * Sets units from either a primitive value or openehr_base.String wrapper.
   */
  set units(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._units = undefined;
    } else if (typeof val === "string") {
      this._units = openehr_base.String.from(val);
    } else {
      this._units = val;
    }
  }

  /**
   * Optional normal range.
   */
  declare normal_range?: undefined;
  /**
   * Optional tagged other reference ranges for this value in its particular measurement context.
   */
  declare other_reference_ranges?: undefined;
  /**
   * Internal storage for units_system
   * @protected
   */
  protected _units_system?: openehr_base.String;

  /**
   * Optional field used to specify a units system from which codes in \`_units_\` are defined. Value is a URI identifying a terminology containing units concepts from the  (https://www.hl7.org/fhir/terminologies-systems.html[HL7 FHIR terminologies list]).
   *
   * If not set, the UCUM standard (case-sensitive codes) is assumed as the units system.
   */
  get units_system(): string | undefined {
    return this._units_system?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for units_system.
   * Use this to access openehr_base.String methods.
   */
  get $units_system(): openehr_base.String | undefined {
    return this._units_system;
  }

  /**
   * Sets units_system from either a primitive value or openehr_base.String wrapper.
   */
  set units_system(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._units_system = undefined;
    } else if (typeof val === "string") {
      this._units_system = openehr_base.String.from(val);
    } else {
      this._units_system = val;
    }
  }

  /**
   * Internal storage for units_display_name
   * @protected
   */
  protected _units_display_name?: openehr_base.String;

  /**
   * Optional field containing the displayable form of the \`_units_\` field, e.g. \`'°C'\`.
   *
   * If not set, the application environment needs to determine the displayable form.
   *
   * NOTE: The display name may be language-dependent for various older and non-systematic units. For this reason, it is not recommended to add unit display names to archetypes, only to templates (for localisation purposes).
   */
  get units_display_name(): string | undefined {
    return this._units_display_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for units_display_name.
   * Use this to access openehr_base.String methods.
   */
  get $units_display_name(): openehr_base.String | undefined {
    return this._units_display_name;
  }

  /**
   * Sets units_display_name from either a primitive value or openehr_base.String wrapper.
   */
  set units_display_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._units_display_name = undefined;
    } else if (typeof val === "string") {
      this._units_display_name = openehr_base.String.from(val);
    } else {
      this._units_display_name = val;
    }
  }

  /**
   * Sum of this \`DV_QUANTITY\` and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override add(other: DV_QUANTITY): DV_QUANTITY {
    // Check units compatibility
    if (this.units !== other.units) {
      throw new Error("Cannot add quantities with different units");
    }
    
    const result = new DV_QUANTITY();
    result.magnitude = (this.magnitude || 0) + (other.magnitude || 0);
    result.units = this.units;
    result.units_system = this.units_system;
    result.precision = this.precision;
    
    const thisAcc = accuracyAsNumber(this.accuracy);
    const otherAcc = accuracyAsNumber(other.accuracy);
    if (thisAcc !== undefined && otherAcc !== undefined) {
      result.accuracy = openehr_base.Real.from(thisAcc + otherAcc);
    }

    return result;
  }

  /**
   * Difference of this \`DV_QUANTITY\` and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override subtract(other: DV_QUANTITY): DV_QUANTITY {
    // Check units compatibility
    if (this.units !== other.units) {
      throw new Error("Cannot subtract quantities with different units");
    }
    
    const result = new DV_QUANTITY();
    result.magnitude = (this.magnitude || 0) - (other.magnitude || 0);
    result.units = this.units;
    result.units_system = this.units_system;
    result.precision = this.precision;
    
    const thisAcc = accuracyAsNumber(this.accuracy);
    const otherAcc = accuracyAsNumber(other.accuracy);
    if (thisAcc !== undefined && otherAcc !== undefined) {
      result.accuracy = openehr_base.Real.from(
        Math.sqrt(thisAcc ** 2 + otherAcc ** 2),
      );
    }

    return result;
  }

  /**
   * Product of this \`DV_QUANTITY\` and \`_factor_\`.
   * @param factor - Parameter
   * @returns Result value
   */
  override multiply(factor: number): DV_QUANTITY {
    const result = new DV_QUANTITY();
    result.magnitude = (this.magnitude || 0) * factor;
    result.units = this.units;
    result.units_system = this.units_system;
    result.precision = this.precision;

    const thisAcc = accuracyAsNumber(this.accuracy);
    if (thisAcc !== undefined) {
      result.accuracy = openehr_base.Real.from(thisAcc * Math.abs(factor));
    }

    return result;
  }

  /**
   * True if this Quantity object is less than \`_other_\`, based on comparison of \`_magnitude_\`. Only valid if \`_is_strictly_comparable_to()_\` is True.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_QUANTITY): openehr_base.Boolean {
    // Should check units compatibility but comparison is still possible
    if (this.units !== other.units) {
      console.warn("Comparing quantities with different units");
    }
    return openehr_base.Boolean.from((this.magnitude || 0) < (other.magnitude || 0));
  }

  /**
   * True if \`_precision_\` = 0, meaning that the \`_magnitude_\` is a whole number.
   * @returns Result value
   */
  is_integral(): openehr_base.Boolean {
    return openehr_base.Boolean.from(this.precision === 0);
  }

  /**
   * True if this quantity and \`_other_\` have the same \`_units_\` and also \`_units_system_\` if it exists.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    if (!(other instanceof DV_QUANTITY)) {
      return openehr_base.Boolean.from(false);
    }
    
    // Check units match
    if (this.units !== other.units) {
      return openehr_base.Boolean.from(false);
    }
    
    // If units_system is present, it must match too
    if (this.units_system !== undefined || other.units_system !== undefined) {
      if (this.units_system !== other.units_system) {
        return openehr_base.Boolean.from(false);
      }
    }
    
    return openehr_base.Boolean.from(true);
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_QUANTITY)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare magnitude and units
    return openehr_base.Boolean.from(
      this.magnitude === other.magnitude && this.units === other.units
    );
  }
}

/**
 * Countable quantities. Used for countable types such as pregnancies and steps (taken by a physiotherapy patient), number of cigarettes smoked in a day.
 *
 * Misuse: Not to be used for amounts of physical entities (which all have units).
 */
export class DV_COUNT extends DV_AMOUNT {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for magnitude
   * @protected
   */
  protected _magnitude?: openehr_base.Integer64;

  get magnitude(): number | undefined {
    return this._magnitude?.value;
  }

  /**
   * Gets the openehr_base.Integer64 wrapper object for magnitude.
   * Use this to access openehr_base.Integer64 methods.
   */
  get $magnitude(): openehr_base.Integer64 | undefined {
    return this._magnitude;
  }

  /**
   * Sets magnitude from either a primitive value or openehr_base.Integer64 wrapper.
   */
  set magnitude(val: number | openehr_base.Integer64 | undefined) {
    if (val === undefined || val === null) {
      this._magnitude = undefined;
    } else if (typeof val === "number") {
      this._magnitude = openehr_base.Integer64.from(val);
    } else {
      this._magnitude = val;
    }
  }

  /**
   * Optional normal range.
   */
  declare normal_range?: undefined;
  /**
   * Optional tagged other reference ranges for this value in its particular measurement context.
   */
  declare other_reference_ranges?: undefined;

  override ordered_magnitude(): openehr_base.Integer64 {
    return this._magnitude ?? openehr_base.Integer64.from(0);
  }

  /**
   * Sum of this \`DV_COUNT\` and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override add(other: DV_COUNT): DV_COUNT {
    const result = new DV_COUNT();
    result.magnitude = (this.magnitude || 0) + (other.magnitude || 0);
    
    const thisAcc = accuracyAsNumber(this.accuracy);
    const otherAcc = accuracyAsNumber(other.accuracy);
    if (thisAcc !== undefined && otherAcc !== undefined) {
      result.accuracy = openehr_base.Integer.from(Math.round(thisAcc + otherAcc));
    }

    return result;
  }

  /**
   * Difference of this \`DV_COUNT\` and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override subtract(other: DV_COUNT): DV_COUNT {
    const result = new DV_COUNT();
    result.magnitude = (this.magnitude || 0) - (other.magnitude || 0);
    
    const thisAcc = accuracyAsNumber(this.accuracy);
    const otherAcc = accuracyAsNumber(other.accuracy);
    if (thisAcc !== undefined && otherAcc !== undefined) {
      result.accuracy = openehr_base.Integer.from(
        Math.round(Math.sqrt(thisAcc ** 2 + otherAcc ** 2)),
      );
    }

    return result;
  }

  /**
   * Product of this \`DV_COUNT\` and \`_factor_\`.
   * @param factor - Parameter
   * @returns Result value
   */
  override multiply(factor: number): DV_COUNT {
    const result = new DV_COUNT();
    result.magnitude = Math.round((this.magnitude || 0) * factor);
    
    const thisAcc = accuracyAsNumber(this.accuracy);
    if (thisAcc !== undefined) {
      result.accuracy = openehr_base.Integer.from(
        Math.round(thisAcc * Math.abs(factor)),
      );
    }

    return result;
  }

  /**
   * True if this Quantified object is less than \`_other_\`, based on comparison of \`_magnitude_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: DV_COUNT): openehr_base.Boolean {
    return openehr_base.Boolean.from((this.magnitude || 0) < (other.magnitude || 0));
  }

  /**
   * Return True.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Counts are always strictly comparable to other counts
    return openehr_base.Boolean.from(other instanceof DV_COUNT);
  }

  /**
   * Value equality comparison.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: DV_QUANTIFIED): openehr_base.Boolean {
    if (!(other instanceof DV_COUNT)) {
      return openehr_base.Boolean.from(false);
    }
    // Compare magnitude
    return openehr_base.Boolean.from(this.magnitude === other.magnitude);
  }
}

/**
 * Abstract class defining the concept of quantified entities whose values are absolute with respect to an origin. Dates and Times are the main example.
 */
export abstract class DV_ABSOLUTE_QUANTITY extends DV_QUANTIFIED {
  override accuracy?: openehr_base.Any = undefined;
  /**
   * Addition of a differential amount to this quantity.
   *
   * The value of accuracy in the result is either:
   *
   * * the sum of the accuracies of the operands, if both present, or;
   * * unknown, if either or both operand accuracies are unknown.
   * @param a_diff - Parameter
   * @returns Result value
   */
  abstract add(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY;

  /**
   * Result of subtracting a differential amount from this quantity.
   *
   * The value of \`_accuracy_\` in the result is either:
   *
   * * the sum of the accuracies of the operands, if both present, or;
   * * unknown, if either or both operand accuracies are unknown.
   * @param a_diff - Parameter
   * @returns Result value
   */
  abstract subtract(a_diff: DV_AMOUNT): DV_ABSOLUTE_QUANTITY;

  /**
   * Difference of two quantities.
   *
   * The value of accuracy in the result is either:
   *
   * * the sum of the accuracies of the operands, if both present, or;
   * * unknown, if either or both operand accuracies are unknown.
   * @param other - Parameter
   * @returns Result value
   */
  abstract diff(other: DV_ABSOLUTE_QUANTITY): DV_AMOUNT;
}

/**
 * A data type that represents integral score values, e.g. pain, Apgar values, etc, where there is:
 *
 * a) implied ordering,
 * b) no implication that the distance between each value is constant, and
 * c) the total number of values is finite;
 * d) integer values only.
 *
 * Note that although the term 'ordinal' in mathematics means natural numbers only, here any integer is allowed, since negative and zero values are often used by medical professionals for values around a neutral point. Examples of sets of ordinal values:
 *
 * *   -3, -2, -1, 0, 1, 2, 3  -- reflex response values
 * *    0, 1, 2                  -- Apgar values
 *
 * This class is used for recording any clinical datum which is customarily recorded using symbolic values. Example: the results on a urinalysis strip, e.g. \`{neg, trace, +, ++, +++}\` are used for leucocytes, protein, nitrites etc; for non-haemolysed blood \`{neg, trace, moderate}\`; for haemolysed blood \`{small, moderate, large}\`.
 *
 * For scores or scales that include Real numbers (or might in the future, i.e. not fixed for all time, such as Apgar), use \`DV_SCALE\`. \`DV_SCALE\` may also be used in future for representing purely Integer-based scales, however, the \`DV_ORDINAL\` type should continue to be supported in software implementations in order to accommodate existing data that are instances of this type.
 */
export class DV_ORDINAL extends DV_ORDERED {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Coded textual representation of this value in the enumeration, which may be strings made from  +  symbols, or other enumerations of terms such as  \`mild\`, \`moderate\`, \`severe\`, or even the same number series as the values, e.g. 1, 2, 3.
   */
  symbol?: DV_CODED_TEXT;
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.Integer;

  /**
   * Value in ordered enumeration of values. Any integer value can be used.
   */
  get value(): number | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for value.
   * Use this to access openehr_base.Integer methods.
   */
  get $value(): openehr_base.Integer | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.Integer wrapper.
   */
  set value(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "number") {
      this._value = openehr_base.Integer.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * True if this Ordinal value is less than \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: DV_ORDINAL): openehr_base.Boolean {
    // Compare based on integer values
    return openehr_base.Boolean.from((this.value || 0) < (other.value || 0));
  }

  /**
   * Test if this Ordinal is strictly comparable to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Ordinals from the same terminology/code system are strictly comparable
    // Check if other is a DV_ORDINAL instance
    // A more thorough implementation would check the terminology_id of the symbols
    return openehr_base.Boolean.from(other instanceof DV_ORDINAL);
  }
}

/**
 * A data type that represents scale values, where there is:
 *
 * a) implied ordering,
 * b) no implication that the distance between each value is constant, and
 * c) the total number of values is finite;
 * d) non-integer values are allowed.
 *
 * Example:
 *
 * ----
 * Borg CR 10 Scale
 *
 * 0    No Breathlessness at all
 * 0.5  Very Very Slight (Just Noticeable)
 * 1    Very Slight
 * 2    Slight Breathlessness
 * 3    Moderate
 * ... etc
 * ----
 *
 * For scores that include only Integers, \`DV_SCALE\` may also be used, but \`DV_ORDINAL\` should be supported to accommodate existing data instances of that type.
 */
export class DV_SCALE extends DV_ORDERED {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Coded textual representation of this value in the scale range, which may be strings made from symbols or other enumerations of terms such as  \`no breathlessness\`, \`very very slight\`, \`slight breathlessness\`. Codes come from archetypes.
   *
   * In some cases, a scale may include values that have no code/symbol. In this case, the symbol will be a \`DV-CODED_TEXT\` including the \`_terminology_id_\` and a blank String value for \`_code_string_\`.
   */
  symbol?: DV_CODED_TEXT;
  /**
   * Real number value of Scale item.
   */
  value?: number;
  /**
   * Test if this Scale value is strictly comparable to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  is_strictly_comparable_to(other: DV_ORDERED): openehr_base.Boolean {
    // Scales from the same scale definition are strictly comparable
    // Check if other is a DV_SCALE instance
    // A more thorough implementation would check the terminology_id of the symbols
    return openehr_base.Boolean.from(other instanceof DV_SCALE);
  }

  /**
   * True if this Scale value is less than \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: DV_SCALE): openehr_base.Boolean {
    // Compare based on numeric values
    return openehr_base.Boolean.from((this.value || 0) < (other.value || 0));
  }
}
